import { useEffect, useRef, useState, useCallback } from "react";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Image as ImageIcon,
  Video as VideoIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { usePageTranslation } from "@/i18n/useTranslation";
import { supportTranslations as enSupport } from "@/i18n/locales/en/support";
import { supportTranslations as thSupport } from "@/i18n/locales/th/support";

/**
 * Zero-dependency rich text composer (contentEditable) for the mock milestone.
 *
 * - Formatting (bold/italic/underline/lists) via document.execCommand.
 * - Inline image & video upload: files are read as object URLs and inserted at
 *   the caret as <img>/<video> nodes, so media appears inline in the body.
 * - Emits the current HTML via onChange. Uncontrolled internally (React never
 *   re-writes innerHTML) so the caret never jumps.
 *
 * When the real backend lands we swap this internal for Tiptap (the app's
 * standard editor) — the output stays HTML, so nothing downstream changes.
 */

interface RichComposerProps {
  onChange: (html: string, plainText: string) => void;
  placeholder?: string;
  className?: string;
  /** Tailwind min-height for the editable area (default: min-h-[180px]). */
  minHeightClass?: string;
  /** Seed the editor with existing HTML on mount (used by the edit-message flow). */
  initialHtml?: string;
}

function ToolbarButton({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      // preventDefault on mousedown keeps the editor selection intact
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
    >
      {children}
    </button>
  );
}

export function RichComposer({
  onChange,
  placeholder,
  className,
  minHeightClass = "min-h-[180px]",
  initialHtml,
}: RichComposerProps) {
  const t = usePageTranslation({ en: enSupport, th: thSupport }) as typeof enSupport;
  const editorRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [isEmpty, setIsEmpty] = useState(!(initialHtml && initialHtml.trim()));

  // Seed existing content once on mount (edit flow). Uncontrolled thereafter so
  // the caret never jumps — same contract as the reply composer.
  useEffect(() => {
    const el = editorRef.current;
    if (el && initialHtml && !el.innerHTML) {
      el.innerHTML = initialHtml;
      const text = (el.textContent ?? "").trim();
      const hasMedia = !!el.querySelector("img, video");
      setIsEmpty(text.length === 0 && !hasMedia);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    const text = (el.textContent ?? "").trim();
    const hasMedia = !!el.querySelector("img, video");
    setIsEmpty(text.length === 0 && !hasMedia);
    onChange(html, text);
  }, [onChange]);

  const exec = (command: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false);
    emit();
  };

  const insertHtml = (html: string) => {
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, html);
    emit();
  };

  const handleFiles = (files: FileList | File[] | null, kind: "image" | "video") => {
    if (!files) return;
    Array.from(files).forEach((file) => {
      const url = URL.createObjectURL(file);
      if (kind === "image") {
        insertHtml(
          `<img src="${url}" alt="${file.name}" style="max-width:100%;border-radius:12px;margin:8px 0;" /><p></p>`,
        );
      } else {
        insertHtml(
          `<video src="${url}" controls style="max-width:100%;border-radius:12px;margin:8px 0;"></video><p></p>`,
        );
      }
    });
  };

  /** Insert any image/video files from a paste or drop as inline media. */
  const handleDroppedFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return false;
    const images = Array.from(files).filter((f) => f.type.startsWith("image/"));
    const videos = Array.from(files).filter((f) => f.type.startsWith("video/"));
    if (images.length) handleFiles(images, "image");
    if (videos.length) handleFiles(videos, "video");
    return images.length > 0 || videos.length > 0;
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (handleDroppedFiles(e.clipboardData?.files ?? null)) {
      e.preventDefault();
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (handleDroppedFiles(e.dataTransfer?.files ?? null)) {
      e.preventDefault();
    }
  };

  return (
    <div
      className={cn(
        "rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900/40",
        className,
      )}
    >
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border-b border-gray-100 px-2 py-1.5 dark:border-gray-800">
        <ToolbarButton title={t.toolbarBold} onClick={() => exec("bold")}>
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title={t.toolbarItalic} onClick={() => exec("italic")}>
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title={t.toolbarUnderline} onClick={() => exec("underline")}>
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarButton>
        <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
        <ToolbarButton title={t.toolbarBulletList} onClick={() => exec("insertUnorderedList")}>
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title={t.toolbarNumberedList} onClick={() => exec("insertOrderedList")}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
        <ToolbarButton title={t.toolbarInsertImage} onClick={() => imageInputRef.current?.click()}>
          <ImageIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title={t.toolbarInsertVideo} onClick={() => videoInputRef.current?.click()}>
          <VideoIcon className="h-4 w-4" />
        </ToolbarButton>
      </div>

      {/* Editable area */}
      <div className="relative">
        {isEmpty && placeholder && (
          <div className="pointer-events-none absolute left-4 top-3 text-sm text-gray-400 dark:text-gray-500">
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          role="textbox"
          aria-multiline="true"
          className={cn(
            "support-rich-editor w-full px-4 py-3 text-sm leading-relaxed text-gray-800 outline-none dark:text-gray-100 [&_img]:max-w-full [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_video]:max-w-full",
            minHeightClass,
          )}
        />
      </div>

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files, "image");
          e.target.value = "";
        }}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files, "video");
          e.target.value = "";
        }}
      />
    </div>
  );
}
